import { Loader2, Pencil, Plus, UserCheck, Users, UserX } from "lucide-react";
import { useState } from "react";

import { StatusPill } from "@/components/status-pill";
import { Button } from "@/components/ui/button";
import { type AdminContactPermissions, useUpdateContact } from "@/lib/api";
import { cn } from "@/lib/utils";

import { ContactDialog, type ContactDialogMode } from "./contact-dialog";
import { usePendingIdSet } from "./use-pending-id-set";

interface Props {
  accountId: string;
  contacts: AdminContactPermissions[];
}

export function ContactList({ accountId, contacts }: Props) {
  const updateContact = useUpdateContact(accountId);
  const [dialogMode, setDialogMode] = useState<ContactDialogMode>("create");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<AdminContactPermissions | undefined>(undefined);
  // Track pending activation toggles per contact so two in flight don't share
  // one spinner; use mutateAsync so a superseded toggle still clears its state.
  const pending = usePendingIdSet();
  // The contact awaiting a deactivate confirmation, if any.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const openDialog = (mode: ContactDialogMode, contact?: AdminContactPermissions) => {
    setDialogMode(mode);
    setSelected(contact);
    setDialogOpen(true);
  };

  const setActive = async (contact: AdminContactPermissions, active: boolean) => {
    setConfirmingId(null);
    pending.add(contact.contact_id);
    try {
      await updateContact.mutateAsync({ contactId: contact.contact_id, active });
    } catch {
      // The mutation surfaces errors via its own state; nothing row-specific to show.
    } finally {
      pending.remove(contact.contact_id);
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">Contacts</h3>
        <Button size="sm" onClick={() => openDialog("create")}>
          <Plus className="h-4 w-4" />
          Add contact
        </Button>
      </div>

      <div className="bg-card border-border overflow-hidden rounded-lg border shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-warm-gray-01 border-border border-b">
              <tr>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  Person
                </th>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  Email
                </th>
                <th className="text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase">
                  Status
                </th>
                <th className="text-muted px-4 py-3 text-right text-[10px] font-medium tracking-[0.16em] uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-border divide-y">
              {contacts.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-12 text-center">
                      <Users className="text-muted h-6 w-6" />
                      <p className="text-foreground text-sm font-medium">No contacts yet</p>
                      <p className="text-muted max-w-xs text-xs">
                        Add a person by email so they can connect over MCP.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                contacts.map((contact) => {
                  const isPending = pending.ids.has(contact.contact_id);
                  const isConfirming = confirmingId === contact.contact_id;
                  return (
                    <tr
                      key={contact.contact_id}
                      className={cn("transition-colors", !contact.active && "opacity-60")}
                    >
                      <td className="px-4 py-3 align-middle">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-foreground font-medium">{contact.full_name}</span>
                          {contact.role && <StatusPill>{contact.role}</StatusPill>}
                        </div>
                      </td>
                      <td className="text-muted px-4 py-3 align-middle">
                        {contact.email ?? <span className="text-warm-gray-06">No email</span>}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <StatusPill tone={contact.active ? "green" : "neutral"}>
                          {contact.active ? "Active" : "Inactive"}
                        </StatusPill>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center justify-end gap-1">
                          {isPending ? (
                            <Loader2 className="text-muted h-4 w-4 animate-spin" />
                          ) : isConfirming ? (
                            <>
                              <span className="text-muted mr-1 text-xs">Revoke access?</span>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => void setActive(contact, false)}
                              >
                                Deactivate
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmingId(null)}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDialog("edit", contact)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </Button>
                              {contact.active ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => setConfirmingId(contact.contact_id)}
                                >
                                  <UserX className="h-3.5 w-3.5" />
                                  Deactivate
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => void setActive(contact, true)}
                                >
                                  <UserCheck className="h-3.5 w-3.5" />
                                  Activate
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ContactDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        accountId={accountId}
        mode={dialogMode}
        contact={selected}
      />
    </section>
  );
}
